import { Injectable } from '@angular/core';
import Utils from '../../../app/shared/utils/utils';
import { GameSolutionTypeConfigOptions } from '../../../config/game-solution-type-config.interface';
import { GameSolutionCategory } from '../../game/enum/game-solution-category.enum';
import { FormControlOptions } from '../interfaces/form-control-options.interface';
import { FormControlSelectOptions } from '../interfaces/form-control-select-options.interface';
import { Dictionary } from '../models/dictionary';
import { GameUtilHelper } from '../models/helper/game-util-helper';
import { ConfigurationService } from '../services/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class GameSolutionTypeUtils {

  private solutionTypeValues;

  constructor(private config: ConfigurationService, private readonly gameUtilHelper: GameUtilHelper) {
  }

  private getSolutionTypes(): GameSolutionTypeConfigOptions {
    if (this.config.isLoadingConfig()) {
      return undefined;
    }

    const config = this.config.getSolutionTypeConfig();
    if (config) {
      return Utils.clone(config);
    }

    return undefined;
  }

  getTypeConfig(type: number) {
    return this.getSolutionTypes()[ type ];
  }

  getLabel(solutionType: string | number) {
    if (this.getSolutionTypes() && this.isValid(solutionType)) {
      return this.getSolutionTypes()[ Number(solutionType) ].label;
    }

    return '';
  }

  hasField(solutionType: string | number, field: string): boolean {
    if (this.getSolutionTypes()) {
      return Object.keys(this.getSolutionTypes()[ Number(solutionType) ].fields).indexOf(field) >= 0;
    }

    return false;
  }

  getFieldOptions(solutionType: string | number, field: string, defaultOptions: FormControlOptions<any>): FormControlOptions<any> {
    if (this.getSolutionTypes()) {
      const options = this.hasField(solutionType, field) ? Utils.clone(this.getSolutionTypes()[ Number(solutionType) ].fields[ field ]) : {};

      for (const optionName in defaultOptions) {
        if (Utils.isDefined(defaultOptions[ optionName ])) {
          options[ optionName ] = Utils.isDefined(options[ optionName ]) ? options[ optionName ] : defaultOptions[ optionName ];
        }
      }

      options.key = defaultOptions.key || field;
      options.hidden = Utils.isDefined(defaultOptions.hidden) ? defaultOptions.hidden : !this.hasField(solutionType, field);

      return options;
    }

    return {};
  }

  getTypeValues(): number[] {
    if (this.getSolutionTypes()) {
      this.solutionTypeValues = this.solutionTypeValues || Object.keys(this.getSolutionTypes())
        .filter(Number)
        .map(solutionType => Number(solutionType));

      return Utils.clone(this.solutionTypeValues);
    }

    return [];
  }

  getCategoryOfType(type: number): string {
    const config = this.getSolutionTypes()[ type ];

    return config && config.category;
  }

  getCategoryValues(): string[] {
    return Object.values(GameSolutionCategory);
  }

  getDefaultType(): number {
    return this.getTypeValues()[ 0 ];
  }

  getDefaultCategory(): string {
    return this.getCategoryValues()[ 0 ];
  }

  isValid(solutionType: string | number): boolean {
    return !isNaN(Number(solutionType)) && this.getTypeValues().indexOf(Number(solutionType)) >= 0;
  }

  getTypeSelectOptions(): FormControlSelectOptions<number> {
    return this.getTypeValues()
      .map(solutionType => {
        return {
          value: solutionType,
          label: this.getLabel(solutionType)
        };
      });
  }

  getCategorySelectOptions(): FormControlSelectOptions<string> {
    return this.getCategoryValues()
      .map(value => ({ value, label: value.toUpperCase()}));
  }

  getModules(solutionType: string | number): string[] {
    if (this.getSolutionTypes()) {
      return Utils.clone(this.getSolutionTypes()[ Number(solutionType) ].modules);
    }

    return [];
  }

  isModuleEnabled(solutionType: string | number, module: string): boolean {
    return this.getModules(solutionType).indexOf(module) >= 0;
  }

  createDictionary(levelType: string | number, key: 'parameters' | 'textDictionary' | 'assetDictionary'): any[] {

    const types = this.getSolutionTypes();

    if (levelType && types) {
      const dict: Dictionary<any> = types[ Number(levelType) ];

      return this.gameUtilHelper.createDictionary(dict, key);
    }

    return [];
  }
}
